import {Button, Card, Table, type Column} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {without} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, useBoolean, Nullish, isNotNilOrEmpty} from 'shared/web';

import {CampaignFormValues} from '../campaignFormSchema';
import {CampaignAddVehicleDialog} from './CampaignAddVehicleDialog';
import {CampaignManuallySelectedVehicleRow} from './CampaignManuallySelectedVehicleRow';

const IS_INCLUDED_BY_DEFAULT = true;
const IN_CAMPAIGN_COLUMN_WIDTH = 30;
const ACTION_COLUMN_WIDTH = 12;

interface CampaignManuallySelectedVehiclesProps extends RequiredTestIdProps {
  value: CampaignFormValues['manuallySelectedVehicles'] | Nullish;
  onChange: (manuallySelectedVehicles: CampaignFormValues['manuallySelectedVehicles']) => void;
}

export function CampaignManuallySelectedVehicles(props: CampaignManuallySelectedVehiclesProps) {
  const [isDialogOpen, openDialog, closeDialog] = useBoolean();

  const handleConfirm = (vehicleIds: string[]) => {
    const alreadySelectedIds = props.value?.map((vehicle) => vehicle.vehicleId) ?? [];
    const newlySelected = without(alreadySelectedIds, vehicleIds).map((vehicleId) => ({
      vehicleId,
      isIncluded: IS_INCLUDED_BY_DEFAULT,
    }));

    props.onChange([...(props.value ?? []), ...newlySelected]);
  };

  const handleRemove = (vehicleId: string) =>
    props.onChange(props.value?.filter((vehicle) => vehicle.vehicleId !== vehicleId) ?? []);

  const handleIsIncludedChange = (vehicleId: string, isIncluded: boolean) => {
    props.onChange(
      props.value?.map((vehicle) =>
        vehicle.vehicleId === vehicleId ? {...vehicle, isIncluded} : vehicle
      ) ?? []
    );
  };

  const tableColumns: Column[] = [
    {
      element: (
        <Text size="small" color="secondary" align="left">
          {i18n.t('general.labels.vehicle')}
        </Text>
      ),
    },
    {
      element: (
        <Text size="small" color="secondary" align="right">
          {i18n.t('entity.salesCampaign.labels.inCampaign')}
        </Text>
      ),
      width: IN_CAMPAIGN_COLUMN_WIDTH,
    },
    {width: ACTION_COLUMN_WIDTH},
  ];

  return (
    <VStack spacing={4}>
      <HStack>
        <Button
          variant="outlined"
          leftIcon="content/add"
          title={i18n.t('entity.salesCampaign.actions.addManualVehicles')}
          onClick={openDialog}
          data-testid={suffixTestId('add', props)}
        />
      </HStack>

      <Show when={isNotNilOrEmpty(props.value)}>
        <Card
          title={i18n.t('entity.salesCampaign.labels.manuallySelectedVehicles')}
          data-testid={suffixTestId('card', props)}
        >
          <VStack spacing={3}>
            <Text size="xSmall" color="tertiary" data-testid={suffixTestId('hint', props)}>
              {i18n.t('entity.salesCampaign.hints.manuallySelectedVehicles')}
            </Text>
            <Table columns={tableColumns} data-testid={suffixTestId('table', props)}>
              {props.value?.map((selected, index) => (
                <CampaignManuallySelectedVehicleRow
                  key={selected.vehicleId}
                  vehicleId={selected.vehicleId}
                  isIncluded={selected.isIncluded}
                  onIsIncludedChange={(isIncluded) =>
                    handleIsIncludedChange(selected.vehicleId, isIncluded)
                  }
                  onRemove={() => handleRemove(selected.vehicleId)}
                  data-testid={suffixTestId(`vehicle-[${index}]`, props)}
                />
              ))}
            </Table>
          </VStack>
        </Card>
      </Show>

      <CampaignAddVehicleDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        data-testid={suffixTestId('picker', props)}
      />
    </VStack>
  );
}
